import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, Copy, ExternalLink, KeyRound, Link2, Loader2, Mail, RefreshCw, Search, ShieldOff, Trash2, UserCheck, Users, X } from "lucide-react";
import { authedFetch } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { PersistentLayout } from "./EditableLayoutRow";

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const duration = (session) => {
  if (!session.started_at || !session.last_seen_at) return "—";
  const seconds = Math.max(0, Math.round((new Date(session.last_seen_at) - new Date(session.started_at)) / 1000));
  if (seconds < 60) return seconds + " sec";
  const minutes = Math.floor(seconds / 60);
  return minutes < 60 ? minutes + " min" : Math.floor(minutes / 60) + " hr " + (minutes % 60) + " min";
};

export const AdminDemos = () => {
  const location = useLocation();
  const selectedSessionId = new URLSearchParams(location.search).get("demoSession");
  const [links, setLinks] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [acting, setActing] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [sort, setSort] = useState("newest");

  const load = async () => {
    setLoading(true);
    const [linkResponse, sessionResponse] = await Promise.all([
      authedFetch("/admin/demo-links"),
      authedFetch("/admin/demo-sessions"),
    ]);
    setLinks(linkResponse.ok ? await linkResponse.json() : []);
    setSessions(sessionResponse.ok ? await sessionResponse.json() : []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const accountSessions = useMemo(() => sessions.filter((session) => session.source_type === "ACCOUNT"), [sessions]);
  const customSessions = useMemo(() => sessions.filter((session) => session.source_type === "CUSTOM_LINK"), [sessions]);
  const filterEntries = (items, dateField) => {
    const needle = query.trim().toLowerCase();
    const now = new Date();
    return items.filter((item) => {
      const matches = !needle || JSON.stringify(item).toLowerCase().includes(needle);
      if (dateRange === "all") return matches;
      const date = new Date(item[dateField] || item.created_at || 0);
      if (dateRange === "today") return matches && date.toDateString() === now.toDateString();
      return matches && date >= new Date(now.getTime() - Number(dateRange) * 86400000);
    }).sort((a, b) => {
      if (sort === "name") return JSON.stringify(a).localeCompare(JSON.stringify(b));
      const ad = new Date(a[dateField] || a.created_at || 0);
      const bd = new Date(b[dateField] || b.created_at || 0);
      return sort === "oldest" ? ad - bd : bd - ad;
    });
  };
  const visibleAccountSessions = filterEntries(accountSessions, "started_at");
  const visibleCustomSessions = filterEntries(customSessions, "started_at");
  const visibleLinks = filterEntries(links, "created_at");

  const generate = async (event) => {
    event.preventDefault();
    setCreating(true);
    const response = await authedFetch("/admin/demo-links", { method: "POST", body: JSON.stringify({ recipient_email: email }) });
    setCreating(false);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      toast.error(body.detail || "Could not generate the demo link.");
      return;
    }
    const created = await response.json();
    setLastGenerated(created);
    setLinks((items) => [created, ...items]);
    setEmail("");
    toast.success("Unique demo link generated.");
  };

  const copyLink = async (link) => {
    await navigator.clipboard.writeText(link.access_url);
    toast.success("Demo link copied.");
  };
  const generateEmail = (link) => {
    const recipient = String(link.issued_to_email || "").replace(/[\r\n]/g, "").trim();
    const suggestedName = recipient.split("@")[0].split(/[._-]/)[0].replace(/^./, (letter) => letter.toUpperCase());
    const enteredName = window.prompt("Recipient first name for the greeting:", suggestedName);
    if (enteredName === null) return;
    const name = enteredName.trim() || "there";
    const subject = "SimplyRefer interactive preview — no sign-up needed";
    const body = [
      `Hi ${name},`,
      "",
      "I hope you're well!",
      "",
      "I tried calling today but wasn't able to reach you and was advised to email.",
      "",
      "I wanted to send through a personalised SimplyRefer interactive preview so you and the GPs in your clinic can click straight in, explore the platform and see how it works without creating an account:",
      "",
      "Open the SimplyRefer interactive preview:",
      link.access_url,
      "",
      "There's no commitment and no patient information required. They can simply have a quick browse and see whether it would be useful for them.",
      "",
      "If any of the GPs find it useful, they can create a free GP account in about 30 seconds. Feel free to reply with any questions or feedback.",
    ].join("\n");
    const mailto = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    toast.success("Opening an editable Outlook draft.");
  };

  const updateLink = async (link, action) => {
    setActing(link.id + action);
    const options = action === "extend" ? { method: "POST", body: JSON.stringify({ days: 30 }) } : { method: "POST" };
    const response = await authedFetch("/admin/demo-links/" + link.id + "/" + action, options);
    setActing(null);
    if (!response.ok) { toast.error("Could not update this demo link."); return; }
    const updated = await response.json();
    setLinks((items) => items.map((item) => item.id === link.id ? { ...item, ...updated } : item));
    toast.success(action === "revoke" ? "Demo access revoked." : "Demo access extended by 30 days.");
  };

  const deleteLink = async (link) => {
    const confirmed = window.confirm("Permanently delete the demo link for " + link.issued_to_email + "? This also removes all session and activity history for this link. This cannot be undone.");
    if (!confirmed) return;
    setActing(link.id + "delete");
    const response = await authedFetch("/admin/demo-links/" + link.id, { method: "DELETE" });
    setActing(null);
    if (!response.ok) { toast.error("Could not delete this demo link."); return; }
    setLinks((items) => items.filter((item) => item.id !== link.id));
    setSessions((items) => items.filter((item) => item.demo_link_id !== link.id));
    if (lastGenerated?.id === link.id) setLastGenerated(null);
    toast.success("Demo link and its activity history deleted.");
  };

  const deleteSession = async (session) => {
    if (!window.confirm(`Permanently delete this viewer session and its activity history? This cannot be undone.`)) return;
    setActing(session.id + "delete");
    const response = await authedFetch("/admin/demo-sessions/" + session.id, { method: "DELETE" });
    setActing(null);
    if (!response.ok) return toast.error("Could not delete this viewer record.");
    setSessions((items) => items.filter((item) => item.id !== session.id));
    toast.success("Viewer record permanently deleted.");
  };

  return (
    <section id="demos" className="scroll-mt-28" data-testid="admin-demos-section">
      <div className="flex items-center gap-2">
        <Link2 className="h-5 w-5 text-primary" />
        <h2 className="font-heading text-xl font-semibold text-slate-900">Demo access and activity</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">Account-based Sample Directory use and private sales-link activity are kept separate.</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => setSearchOpen(!searchOpen)} aria-label="Search demo activity">{searchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}</Button>
        {searchOpen && <Input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search GP, email or demo details…" className="w-64" />}
        <select value={dateRange} onChange={(event) => setDateRange(event.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="all">Any date</option><option value="today">Today</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option></select>
        <select value={sort} onChange={(event) => setSort(event.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="newest">Newest first</option><option value="oldest">Oldest first</option><option value="name">Name/email A–Z</option></select>
      </div>

      {loading ? (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-slate-200 bg-white p-5 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading demo activity…</div>
      ) : (
        <PersistentLayout
          scope="admin-demos-sections"
          direction="vertical"
          className="mt-5 flex flex-col gap-4"
          items={[
            { id: "registered-gp-demo-activity", label: "Registered GP demo activity", node: (
          <details className="group rounded-md border border-slate-200 bg-white" open={!!selectedSessionId && visibleAccountSessions.some((item) => item.id === selectedSessionId)}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary text-secondary-foreground"><UserCheck className="h-4 w-4" /></div>
                <div><h3 className="font-heading font-semibold text-slate-900">Signed-up GP Sample Directory activity</h3><p className="text-xs text-slate-500">{visibleAccountSessions.length} browser session{visibleAccountSessions.length === 1 ? "" : "s"}</p></div>
              </div>
              <ChevronDown className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-180" />
            </summary>
            <div className="border-t border-slate-100 p-5">
              {visibleAccountSessions.length === 0 ? <Empty text="No signed-up GP has opened Sample Directory yet." /> : visibleAccountSessions.map((session) => <SessionCard key={session.id} session={session} selected={session.id === selectedSessionId} onDelete={deleteSession} deleting={acting === session.id + "delete"} />)}
            </div>
          </details>
            ) },
            { id: "custom-demo-links", label: "Custom demo links", node: (
          <details className="group rounded-md border border-slate-200 bg-white" open={!!selectedSessionId && visibleCustomSessions.some((item) => item.id === selectedSessionId)}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-50 text-primary"><Users className="h-4 w-4" /></div>
                <div><h3 className="font-heading font-semibold text-slate-900">Custom sales demo links</h3><p className="text-xs text-slate-500">{visibleLinks.length} link{visibleLinks.length === 1 ? "" : "s"} · {visibleCustomSessions.length} session{visibleCustomSessions.length === 1 ? "" : "s"}</p></div>
              </div>
              <ChevronDown className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-180" />
            </summary>
            <div className="border-t border-slate-100 p-5">
              <form onSubmit={generate} className="rounded-md border border-blue-100 bg-blue-50/60 p-4" data-testid="generate-demo-link-form">
                <Label htmlFor="demo-recipient-email">Recipient email</Label>
                <p className="mt-1 text-xs text-slate-500">The visitor will not be asked for an email. This address identifies who you originally sent the unique link to.</p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <Input id="demo-recipient-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="practice.manager@example.com" data-testid="demo-recipient-email" />
                  <Button type="submit" disabled={creating} data-testid="generate-demo-link-button">{creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />} Generate link</Button>
                </div>
                {lastGenerated && (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm">
                    <span className="min-w-0 truncate text-emerald-800">Generated for <strong>{lastGenerated.issued_to_email}</strong></span>
                    <Button type="button" size="sm" variant="outline" onClick={() => copyLink(lastGenerated)}><Copy className="mr-2 h-4 w-4" /> Copy link</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => generateEmail(lastGenerated)}><Mail className="mr-2 h-4 w-4" /> Generate email</Button>
                  </div>
                )}
              </form>

              <div className="mt-5 space-y-4">
                {visibleLinks.length === 0 ? <Empty text="No custom demo links have been generated yet." /> : visibleLinks.map((link) => {
                  const linkSessions = visibleCustomSessions.filter((session) => session.demo_link_id === link.id);
                  const latest = linkSessions[0];
                  return (
                    <details key={link.id} className="group/link rounded-md border border-slate-200" open={linkSessions.some((item) => item.id === selectedSessionId)}>
                      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-4 py-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /><span className="font-medium text-slate-900">{link.issued_to_email}</span><Status status={link.status} /></div>
                          <p className="mt-1 text-xs text-slate-500">{linkSessions.length} session{linkSessions.length === 1 ? "" : "s"} · Last opened {latest ? fmtDateTime(latest.started_at) : "never"}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={(event) => { event.preventDefault(); copyLink(link); }}><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy</Button>
                  <Button type="button" size="sm" variant="outline" onClick={(event) => { event.preventDefault(); generateEmail(link); }}><Mail className="mr-1.5 h-3.5 w-3.5" /> Generate email</Button>
                          {link.status === "ACTIVE" ? (
                            <Button type="button" size="sm" variant="outline" disabled={!!acting} onClick={(event) => { event.preventDefault(); updateLink(link, "revoke"); }}><ShieldOff className="mr-1.5 h-3.5 w-3.5" /> Revoke</Button>
                          ) : (
                            <Button type="button" size="sm" variant="outline" disabled={!!acting} onClick={(event) => { event.preventDefault(); updateLink(link, "extend"); }}><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Reactivate 30d</Button>
                          )}
                          <Button type="button" size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" disabled={!!acting} onClick={(event) => { event.preventDefault(); deleteLink(link); }}><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete</Button>
                          <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open/link:rotate-180" />
                        </div>
                      </summary>
                      <div className="border-t border-slate-100 p-4">
                        <div className="mb-3 grid grid-cols-1 gap-2 text-xs text-slate-500 sm:grid-cols-3">
                          <span>Created {fmtDateTime(link.created_at)}</span><span>Expires {fmtDateTime(link.expires_at)}</span><span>Issued by {link.created_by_email || "—"}</span>
                        </div>
                        {linkSessions.length === 0 ? <Empty text="This link has not been opened." /> : linkSessions.map((session) => <SessionCard key={session.id} session={session} selected={session.id === selectedSessionId} onDelete={deleteSession} deleting={acting === session.id + "delete"} />)}
                      </div>
                    </details>
                  );
                })}
              </div>
            </div>
          </details>
            ) },
          ]}
        />
      )}
    </section>
  );
};

const SessionCard = ({ session, selected, onDelete, deleting }) => {
  const events = Array.isArray(session.demo_events) ? [...session.demo_events].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)) : [];
  const pages = [...new Set(events.filter((event) => event.event_type === "PAGE_VIEW").map((event) => event.page_path).filter(Boolean))];
  const label = session.source_type === "ACCOUNT" ? session.auth_email : (session.demo_links?.issued_to_email || "Custom link visitor");
  return (
    <details open={selected} className={"mb-3 rounded-md border p-0 last:mb-0 " + (selected ? "border-blue-400 bg-blue-50/40 ring-2 ring-blue-100" : "border-slate-200 bg-white")} id={"demo-session-" + session.id}>
      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div><p className="font-medium text-slate-900">{label || "Unknown account"}</p><p className="text-xs text-slate-500">Opened {fmtDateTime(session.started_at)} · Active time {duration(session)}</p></div>
        <div className="flex flex-wrap items-center gap-2 text-xs"><Pill icon={ExternalLink} text={(pages.length || 0) + " page" + (pages.length === 1 ? "" : "s")} /><Pill icon={KeyRound} text={(session.token_count || 0) + " reference" + (session.token_count === 1 ? "" : "s")} />{session.create_account_clicked && <Pill icon={UserCheck} text="Create account clicked" accent />}<Button type="button" size="sm" variant="outline" className="border-red-200 text-red-700" disabled={deleting} onClick={(event) => { event.preventDefault(); onDelete(session); }}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete viewer record</Button></div>
      </summary>
      <div className="border-t border-slate-100 px-4 py-3">
        <div className="grid grid-cols-1 gap-2 text-xs text-slate-500 sm:grid-cols-3"><span>Last active {fmtDateTime(session.last_seen_at)}</span><span>Current page {session.current_page || "—"}</span><span>Session {session.ended_at ? "ended " + fmtDateTime(session.ended_at) : "active / not explicitly ended"}</span></div>
        <div className="mt-3 rounded-md bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Activity breakdown</p>
          {events.length === 0 ? <p className="mt-2 text-xs text-slate-500">No detailed events recorded.</p> : <div className="mt-2 space-y-1.5">{events.map((event) => <div key={event.id} className="flex flex-wrap items-center justify-between gap-2 text-xs"><span className="font-medium text-slate-700">{event.event_type.replaceAll("_", " ").toLowerCase()}{event.page_path ? " · " + event.page_path : ""}</span><span className="text-slate-400">{fmtDateTime(event.created_at)}</span></div>)}</div>}
        </div>
      </div>
    </details>
  );
};

const Empty = ({ text }) => <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">{text}</div>;
const Status = ({ status }) => <span className={"rounded-md border px-2 py-0.5 text-xs font-semibold " + (status === "ACTIVE" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-100 text-slate-600")}>{status === "ACTIVE" ? "Active" : "Revoked"}</span>;
const Pill = ({ icon: Icon, text, accent = false }) => <span className={"inline-flex items-center gap-1 rounded-md border px-2 py-1 " + (accent ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-600")}><Icon className="h-3 w-3" /> {text}</span>;
